import { useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";

const interests = [
    "Basic Package",
    "Standard Package",
    "Premium Package",
    "Lead Generation",
    "Content Creation",
    "Not sure yet",
];

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service_interest: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/leads", form);
            toast.success("Thanks! Babur will get back to you within 24 hours.");
            const waMsg = `Hi Babur! I just filled out the contact form.%0A%0AName: ${form.name}%0AInterest: ${form.service_interest || "Not specified"}%0A%0A${form.message}`;
            // Redirect to WhatsApp after short delay
            setTimeout(() => {
                window.open(`https://wa.me/971543676278?text=${waMsg}`, "_blank");
            }, 800);
            setForm({ name: "", email: "", phone: "", service_interest: "", message: "" });
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Something went wrong. Try WhatsApp directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" className="relative py-24 sm:py-32 border-t border-white/10 bg-[#0d0d0f]">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Get in touch
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            Let's build<br />
                            <span className="text-brand">your brand.</span>
                        </h2>
                        <p className="mt-6 text-white/60 leading-relaxed max-w-md">
                            Tell us where your brand is today and where you want it to be. Reply within 24 hours &mdash; or open WhatsApp for a faster route.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href="https://wa.me/971543676278"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={CONTACT.whatsapp}
                                className="group flex items-center justify-between gap-4 border border-white/20 hover:border-brand p-5 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <MessageCircle className="w-5 h-5 text-brand" />
                                    <div>
                                        <div className="font-display font-bold text-white">WhatsApp</div>
                                        <div className="text-xs text-white/50 font-mono">+971 54 367 6278</div>
                                    </div>
                                </div>
                                <span className="text-white/40 group-hover:text-brand transition-colors">→</span>
                            </a>
                            <a
                                href="mailto:baburali514@gmail.com"
                                className="group flex items-center justify-between gap-4 border border-white/20 hover:border-brand p-5 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <Send className="w-5 h-5 text-brand" />
                                    <div>
                                        <div className="font-display font-bold text-white">Email</div>
                                        <div className="text-xs text-white/50 font-mono">baburali514@gmail.com</div>
                                    </div>
                                </div>
                                <span className="text-white/40 group-hover:text-brand transition-colors">→</span>
                            </a>
                        </div>
                    </div>

                    <form
                        onSubmit={submit}
                        data-testid={CONTACT.form}
                        className="lg:col-span-7 bg-[#141414] border border-white/10 p-8 sm:p-10 space-y-5"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2 block">Name *</label>
                                <input
                                    data-testid={CONTACT.name}
                                    type="text"
                                    value={form.name}
                                    onChange={update("name")}
                                    required
                                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-brand outline-none text-white py-3 transition-colors"
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2 block">Email *</label>
                                <input
                                    data-testid={CONTACT.email}
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    required
                                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-brand outline-none text-white py-3 transition-colors"
                                    placeholder="you@example.com"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2 block">Phone</label>
                                <input
                                    data-testid={CONTACT.phone}
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-brand outline-none text-white py-3 transition-colors"
                                    placeholder="+971 ..."
                                />
                            </div>
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2 block">I'm interested in</label>
                                <select
                                    data-testid={CONTACT.interest}
                                    value={form.service_interest}
                                    onChange={update("service_interest")}
                                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-brand outline-none text-white py-3 transition-colors"
                                >
                                    <option value="" className="bg-[#141414]">Choose one</option>
                                    {interests.map((s) => (
                                        <option key={s} value={s} className="bg-[#141414]">{s}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2 block">Tell me about your goals *</label>
                            <textarea
                                data-testid={CONTACT.message}
                                value={form.message}
                                onChange={update("message")}
                                required
                                rows={5}
                                className="w-full bg-transparent border border-white/20 focus:border-brand outline-none text-white p-4 transition-colors resize-none"
                                placeholder="Currently at X followers, want to reach Y in 3 months, main goal is..."
                            />
                        </div>

                        <button
                            type="submit"
                            data-testid={CONTACT.submit}
                            disabled={submitting}
                            className="group w-full inline-flex items-center justify-between gap-3 bg-brand hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-7 py-4 transition-colors"
                        >
                            <span>{submitting ? "Sending..." : "Send message + open WhatsApp"}</span>
                            <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}
